// Single source of truth for campaign-level fields shared between
// the Create and Edit flows. Replaces the parallel implementations in
// CampaignCreate.tsx and CampaignDetail.tsx::OverviewEdit (which
// drifted on filter pickers vs raw JSON between PRs #5 and #6 — see
// docs/outreach-backlog-plan.md A.1).
//
// Step composition is intentionally NOT in this form: step bodies are
// edited from the Steps tab once the campaign exists. The Create flow
// adds a single-step helper *around* this form so admins don't ship an
// empty draft on first save.

import { useMemo, useState } from 'react';

import { useForms } from '../../hooks/useOutreach';
import type {
  AffinityListConfig,
  Campaign,
  CampaignIn,
  CampaignUpdate,
  OutreachSender,
  SendMode,
} from '../../types/outreach';
import styles from '../pages/OutreachPage.module.css';
import type { FilterValue } from './campaignFilters';
import {
  buildCampaignFilter,
  filterToJson,
  hasScopeAxis,
  isNativeList,
  parseFilterJson,
  scopeAxisName,
} from './campaignFilters';
import {
  DEFAULT_SCHEDULE,
  parseSchedule,
  ScheduleFields,
  scheduleToJson,
  validateSchedule,
} from './ScheduleFields';


export type CampaignFormMode = 'create' | 'edit';


/**
 * Caller-supplied submit shape — varies by mode. Create flow returns
 * `CampaignIn` (with `steps` filled by the parent wrapper); Edit flow
 * returns `CampaignUpdate`. Form code itself is shape-agnostic; the
 * mode-specific narrowing happens in `onSubmit`.
 */
export type CampaignFormSubmit =
  | (Omit<CampaignIn, 'steps'> & { _mode: 'create' })
  | (CampaignUpdate & { _mode: 'edit' });


export interface CampaignFormProps {
  mode: CampaignFormMode;
  initial?: Campaign;
  listConfigs: AffinityListConfig[];
  senders: OutreachSender[];
  /**
   * Fired when the user clicks Save. Wrapper handles the actual API
   * call (POST vs PATCH) and any post-save side effects (navigation,
   * step seeding, etc.).
   */
  onSubmit: (body: CampaignFormSubmit) => Promise<void>;
  onCancel: () => void;
  /**
   * Visual customization. Create flow uses 'Create as draft' / 'New
   * campaign'; edit uses 'Save' / 'Edit campaign settings'.
   */
  submitLabel: string;
  title?: string;
  /**
   * Render a slot below the schedule fields and above the action
   * buttons. Create flow uses this to insert the step-1 subject + body
   * inputs without polluting CampaignForm with concerns specific to
   * the M1 helper UX.
   */
  extras?: React.ReactNode;
  isSubmitting?: boolean;
}


export function CampaignForm({
  mode,
  initial,
  listConfigs,
  senders,
  onSubmit,
  onCancel,
  submitLabel,
  title,
  extras,
  isSubmitting,
}: CampaignFormProps) {
  // Initial state — reuses parseSchedule for robustness against legacy
  // shapes (matches what CampaignDetail's Overview render does).
  const initialFilter = useMemo(
    () => parseFilterJson(initial?.affinity_filter_json),
    [initial],
  );
  const initialRsvpOnlyFilter = useMemo(
    () => parseFilterJson(initial?.rsvp_only_filter_json),
    [initial],
  );

  const [name, setName] = useState(initial?.name || '');
  const [senderEmail, setSenderEmail] = useState(initial?.sender_email || '');
  const [listId, setListId] = useState<number | null>(
    initial?.affinity_list_id ?? null,
  );
  // Option ids on affinity lists, option labels on native ones — the
  // picker's value type follows the selected list's source.
  const [statusValues, setStatusValues] = useState<FilterValue[]>(
    initialFilter.status,
  );
  const [scopeValues, setScopeValues] = useState<FilterValue[]>(
    initialFilter.scope,
  );
  const [rsvpOnlyScopeValues, setRsvpOnlyScopeValues] = useState<FilterValue[]>(
    initialRsvpOnlyFilter.scope,
  );
  const [sendMode, setSendMode] = useState<SendMode>(
    initial?.send_mode || 'per_person',
  );
  const [minSeconds, setMinSeconds] = useState<number>(
    initial?.min_seconds_between_sends_per_inbox ?? 67,
  );
  const [trackClicks, setTrackClicks] = useState<boolean>(
    initial?.track_clicks ?? true,
  );
  const [trackOpens, setTrackOpens] = useState<boolean>(
    initial?.track_opens ?? false,
  );
  const [testModeEnabled, setTestModeEnabled] = useState<boolean>(
    initial?.test_mode_enabled ?? false,
  );
  const [ccOverride, setCcOverride] = useState(initial?.cc_override || '');
  const [bccOverride, setBccOverride] = useState(initial?.bcc_override || '');
  const [replyToEmail, setReplyToEmail] = useState(
    initial?.reply_to_email || '',
  );
  const [schedule, setSchedule] = useState(() =>
    initial ? parseSchedule(initial.schedule_json) : { ...DEFAULT_SCHEDULE },
  );
  const [formId, setFormId] = useState<number | null>(initial?.form_id ?? null);
  const [error, setError] = useState<string | null>(null);

  const formsQ = useForms();

  // Read-only-when-not-draft. CampaignDetail/OverviewEdit previously
  // greyed `affinity_list_id` only; expand to sender + send_mode
  // because both break the model when changed mid-flight. (See the
  // Phase A.1 spec block.)
  const isDraft = mode === 'create' || initial?.status === 'draft';
  const lockListSenderMode = !isDraft;

  const selectedList = useMemo<AffinityListConfig | null>(
    () => listConfigs.find((l) => l.affinity_list_id === listId) || null,
    [listConfigs, listId],
  );

  // The picker offers every registered list, so its label can only claim
  // "Affinity" while every list IS an Affinity list. An affinity-only
  // estate (i.e. today) keeps the existing copy exactly.
  const listFieldLabel = listConfigs.some((l) => l.source_kind === 'crm')
    ? 'List'
    : 'Affinity list';
  // Native lists pick option LABELS; affinity lists pick option ids.
  const filterValueKind: FilterValueKind = isNativeList(selectedList)
    ? 'label'
    : 'id';
  const scopeName = scopeAxisName(selectedList);
  // Placeholder copy keeps its own lowercase fallback ("all scope values")
  // while the label falls back to "Scope" — as before.
  const scopeNameInSentence = scopeAxisName(selectedList, 'scope');
  const showScopeFilters = hasScopeAxis(selectedList);

  function changeList(newId: number | null) {
    // Filter values are list-specific (option ids on affinity lists,
    // option labels on native ones); reset on list change so we never
    // silently carry stale values — or the wrong value TYPE — across.
    setListId(newId);
    setStatusValues([]);
    setScopeValues([]);
    setRsvpOnlyScopeValues([]);
  }

  async function handleSave() {
    setError(null);
    if (!name.trim()) return setError('Name is required.');
    if (!senderEmail) return setError('Pick a sender.');
    if (listId == null || !selectedList) return setError('Pick a list.');
    const scheduleErr = validateSchedule(schedule);
    if (scheduleErr) return setError(scheduleErr);
    const replyTo = replyToEmail.trim().toLowerCase();
    if (replyTo && !/^[^@\s]+@ember-infra\.com$/.test(replyTo)) {
      return setError(
        '"Direct replies to" must be a single @ember-infra.com address.',
      );
    }

    // Affinity lists emit {field_id, values:[option_id]}; native lists
    // emit the v2 {field_key, values:[label]} shape the enroller
    // validates. Both live in ./campaignFilters.
    const main = buildCampaignFilter(selectedList, {
      status: statusValues,
      scope: scopeValues,
    });
    if (!main.ok) return setError(main.error);
    const rsvpOnly = buildCampaignFilter(selectedList, {
      scope: rsvpOnlyScopeValues,
    });
    if (!rsvpOnly.ok) return setError(rsvpOnly.error);
    const filterJson = filterToJson(main.filter);
    const rsvpOnlyFilterJson = filterToJson(rsvpOnly.filter);

    try {
      if (mode === 'create') {
        await onSubmit({
          _mode: 'create',
          name: name.trim(),
          sender_email: senderEmail,
          affinity_list_id: listId,
          affinity_filter_json: filterJson,
          rsvp_only_filter_json: rsvpOnlyFilterJson,
          send_mode: sendMode,
          schedule_json: scheduleToJson(schedule),
          min_seconds_between_sends_per_inbox: minSeconds,
          cc_override: ccOverride.trim() || null,
          bcc_override: bccOverride.trim() || null,
          reply_to_email: replyTo || null,
          track_clicks: trackClicks,
          track_opens: trackOpens,
          test_mode_enabled: testModeEnabled,
          form_id: formId,
        });
      } else {
        await onSubmit({
          _mode: 'edit',
          name: name.trim(),
          sender_email: senderEmail,
          affinity_list_id: listId,
          affinity_filter_json: filterJson,
          rsvp_only_filter_json: rsvpOnlyFilterJson,
          send_mode: sendMode,
          schedule_json: scheduleToJson(schedule),
          min_seconds_between_sends_per_inbox: minSeconds,
          cc_override: ccOverride.trim() || null,
          bcc_override: bccOverride.trim() || null,
          reply_to_email: replyTo || null,
          track_clicks: trackClicks,
          track_opens: trackOpens,
          test_mode_enabled: testModeEnabled,
          form_id: formId,
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed.');
    }
  }

  return (
    <div
      className={mode === 'create' ? styles.section : ''}
      style={
        mode === 'edit'
          ? { marginTop: 16, padding: 12, background: '#FAF9F4', borderRadius: 6 }
          : undefined
      }
    >
      {title && (
        <div className={mode === 'create' ? styles.sectionHeader : undefined}>
          {mode === 'create' ? (
            <>
              <h2 className={styles.sectionTitle}>{title}</h2>
              <button type="button" className={styles.button} onClick={onCancel}>
                Cancel
              </button>
            </>
          ) : (
            <h3
              style={{
                margin: '0 0 12px',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 14,
              }}
            >
              {title}
            </h3>
          )}
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {mode === 'edit' && !isDraft && (
        <div
          className={styles.helpText}
          style={{
            marginBottom: 12,
            padding: '8px 12px',
            background: '#FFF7E0',
            border: '1px solid #E8D69A',
            borderRadius: 4,
          }}
        >
          ⚠ This campaign is{' '}
          <strong>{initial?.status}</strong>. Filter changes don't
          auto-re-enroll existing recipients — click <strong>Enroll
          recipients</strong> on the Overview tab to pick up newly-matching
          contacts.
        </div>
      )}

      <div className={styles.formGrid}>
        <label className={styles.label}>Name</label>
        <input
          className={styles.input}
          type="text"
          placeholder="LPAC AGM 2026"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className={styles.label}>Sender</label>
        <select
          className={styles.input}
          value={senderEmail}
          onChange={(e) => setSenderEmail(e.target.value)}
          disabled={lockListSenderMode}
          title={
            lockListSenderMode
              ? 'Locked once the campaign is past draft (changing sender breaks thread continuity for already-sent recipients).'
              : undefined
          }
        >
          <option value="">— select sender —</option>
          {senders.filter((s) => s.active).map((s) => (
            <option key={s.email} value={s.email}>
              {s.email}
              {s.display_name ? ` (${s.display_name})` : ''}
            </option>
          ))}
        </select>

        <label className={styles.label}>{listFieldLabel}</label>
        <select
          className={styles.input}
          value={listId ?? ''}
          onChange={(e) =>
            changeList(e.target.value ? Number(e.target.value) : null)
          }
          disabled={lockListSenderMode}
          title={
            lockListSenderMode
              ? 'Locked once the campaign is past draft (recipients are pre-bound to this list).'
              : undefined
          }
        >
          <option value="">— select list —</option>
          {listConfigs.map((l) => (
            <option key={l.affinity_list_id} value={l.affinity_list_id}>
              {l.label || l.list_slug} ({l.affinity_list_id})
            </option>
          ))}
        </select>

        <label className={styles.label}>Form</label>
        <div>
          <select
            className={styles.input}
            value={formId ?? ''}
            onChange={(e) =>
              setFormId(e.target.value ? Number(e.target.value) : null)
            }
            style={{ width: '100%' }}
          >
            <option value="">— select form —</option>
            {(formsQ.data || []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.name} (/forms/{f.slug})
              </option>
            ))}
          </select>
          <div className={styles.helpText} style={{ marginTop: 4 }}>
            Required before starting the campaign. The form drives the
            RSVP page (headline + which fields show) and where responses
            write back to. Manage forms on the <strong>Forms</strong> tab.
          </div>
        </div>

        <label className={styles.label}>Filter by Status</label>
        <FilterMultiSelect
          options={selectedList?.status_dropdown_options || {}}
          values={statusValues}
          onChange={setStatusValues}
          valueKind={filterValueKind}
          placeholder={
            selectedList
              ? selectedList.status_dropdown_options &&
                Object.keys(selectedList.status_dropdown_options).length > 0
                ? '— include all statuses —'
                : 'No options cached. Refresh on the list config tab.'
              : 'Pick a list first.'
          }
        />

        {selectedList && showScopeFilters ? (
          <>
            <label className={styles.label}>
              Filter by {scopeName}
            </label>
            <FilterMultiSelect
              options={selectedList.scope_dropdown_options || {}}
              values={scopeValues}
              onChange={setScopeValues}
              valueKind={filterValueKind}
              placeholder={
                Object.keys(selectedList.scope_dropdown_options || {}).length > 0
                  ? `— include all ${scopeNameInSentence} values —`
                  : 'No options cached. Click "Refresh options" on the list config.'
              }
            />
          </>
        ) : null}

        {selectedList && showScopeFilters ? (
          <>
            <label className={styles.label}>
              RSVP-only {scopeName}
            </label>
            <div>
              <FilterMultiSelect
                options={selectedList.scope_dropdown_options || {}}
                values={rsvpOnlyScopeValues}
                onChange={setRsvpOnlyScopeValues}
                valueKind={filterValueKind}
                placeholder={
                  Object.keys(selectedList.scope_dropdown_options || {}).length > 0
                    ? `— no RSVP-only ${scopeNameInSentence} values —`
                    : 'No options cached. Click "Refresh options" on the list config.'
                }
              />
              <div className={styles.helpText} style={{ marginTop: 4 }}>
                Enrolled for RSVP forms and calendar queue only; no campaign
                emails are sent.
              </div>
            </div>
          </>
        ) : null}

        <label className={styles.label}>Send mode</label>
        <select
          className={styles.input}
          value={sendMode}
          onChange={(e) => setSendMode(e.target.value as SendMode)}
          disabled={lockListSenderMode}
          title={
            lockListSenderMode
              ? 'Locked once the campaign is past draft (per-person vs per-org changes which merge fields are available in templates).'
              : undefined
          }
        >
          <option value="per_person">Per person (one email per recipient)</option>
          <option value="per_organization">
            Per organization (one email per company; all members on To:)
          </option>
        </select>

        <label className={styles.label}>Throttle (sec/sender)</label>
        <input
          className={styles.input}
          type="number"
          value={minSeconds}
          min={0}
          max={600}
          onChange={(e) => setMinSeconds(Number(e.target.value))}
        />

        <label className={styles.label}>CC override</label>
        <input
          className={styles.input}
          type="text"
          placeholder="cc1@x.com,cc2@x.com (overrides sender's default_cc)"
          value={ccOverride}
          onChange={(e) => setCcOverride(e.target.value)}
        />

        <label className={styles.label}>BCC override</label>
        <input
          className={styles.input}
          type="text"
          placeholder="bcc@x.com"
          value={bccOverride}
          onChange={(e) => setBccOverride(e.target.value)}
        />

        <label className={styles.label}>Direct replies to</label>
        <div>
          <input
            className={styles.input}
            type="text"
            placeholder="ir@ember-infra.com (blank = replies go to sender)"
            value={replyToEmail}
            onChange={(e) => setReplyToEmail(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box' }}
          />
          {replyToEmail.trim() !== '' && (
            <div
              className={styles.helpText}
              style={{
                marginTop: 6,
                padding: '8px 12px',
                background: '#FFF7E0',
                border: '1px solid #E8D69A',
                borderRadius: 4,
              }}
            >
              ⚠ Replies sent to this address may not be recognized by the
              engine — recipients may not be auto-marked as replied, so
              finish-on-reply may not fire and follow-up steps could still
              send. <strong>Avoid on multi-step campaigns without further
              vetting.</strong> Auto-capture requires this mailbox to be a
              registered active sender.
            </div>
          )}
        </div>

        <label className={styles.label}>Click tracking</label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <input
            type="checkbox"
            checked={trackClicks}
            onChange={(e) => setTrackClicks(e.target.checked)}
          />
          Track click-through on body links (unsubscribe URL excluded)
        </label>

        <label className={styles.label}>Open tracking</label>
        <div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <input
              type="checkbox"
              checked={trackOpens}
              onChange={(e) => setTrackOpens(e.target.checked)}
            />
            Track estimated opens from first-party image loads
          </label>
          <div className={styles.helpText} style={{ marginTop: 4 }}>
            Estimated from image loads; includes possible automated prefetches/caching by mail providers.
          </div>
        </div>

        <label className={styles.label}>Test mode</label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <input
            type="checkbox"
            checked={testModeEnabled}
            onChange={(e) => setTestModeEnabled(e.target.checked)}
          />
          Route this campaign's sends to the test recipient (ORs with the
          global Settings toggle)
        </label>
      </div>

      <h3
        style={{
          marginTop: 24,
          marginBottom: 8,
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
        }}
      >
        Send schedule
      </h3>
      <ScheduleFields value={schedule} onChange={setSchedule} />

      {extras}

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 20,
          justifyContent: 'flex-end',
        }}
      >
        <button type="button" className={styles.button} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={`${styles.button} ${styles.buttonPrimary}`}
          onClick={handleSave}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </div>
  );
}


/**
 * What a picked option is worth on the wire: an Affinity option id
 * ('id') or a CRM option label ('label').
 */
export type FilterValueKind = 'id' | 'label';


/**
 * Multi-select picker over a {label: option_id} dict. Renders labels
 * to the user; sends option IDs (affinity lists) or the labels
 * themselves (native lists, whose cached "ids" are placeholder
 * ordinals) to the backend. Used for both the Status and Scope filter
 * slots — same shape, same UX.
 */
export function FilterMultiSelect({
  options,
  values,
  onChange,
  placeholder,
  valueKind = 'id',
}: {
  options: Record<string, number>;
  values: FilterValue[];
  onChange: (next: FilterValue[]) => void;
  placeholder: string;
  valueKind?: FilterValueKind;
}) {
  const entries = Object.entries(options);
  if (entries.length === 0) {
    return <div className={styles.helpText}>{placeholder}</div>;
  }

  function toggle(v: FilterValue) {
    onChange(
      values.includes(v) ? values.filter((x) => x !== v) : [...values, v],
    );
  }

  return (
    <div>
      {values.length === 0 && (
        <div className={styles.helpText} style={{ marginBottom: 4 }}>
          {placeholder}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {entries.map(([label, id]) => {
          const value: FilterValue = valueKind === 'label' ? label : id;
          const selected = values.includes(value);
          return (
            <button
              key={label}
              type="button"
              className={`${styles.button} ${selected ? styles.buttonPrimary : ''}`}
              onClick={() => toggle(value)}
              style={{ fontSize: 12 }}
              title={valueKind === 'label' ? label : `option_id=${id}`}
            >
              {selected ? '✓ ' : ''}
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
